import { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { MultiSelect } from '@/components/MultiSelect';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const scrollToLastMobileCard = _wg('scrollToLastMobileCard');

interface CaseReportClientsInvolvedProps {
    value: any[];
    isReadOnly?: boolean;
    onChange?: (clients: any[]) => void;
    onAddExisting?: (type: string) => void;
    onViewItem?: (type: string, id: any) => void;
    onRemoveItem?: (type: string, item: any) => void;
    onSitesChanged?: (clientId: any, sites: number[]) => void;
}

export function CaseReportClientsInvolved({
    value: clients,
    isReadOnly = false,
    onChange,
    onAddExisting,
    onViewItem,
    onRemoveItem,
    onSitesChanged,
}: CaseReportClientsInvolvedProps) {
    const { theme } = useTheme();
    const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; item: any }>({ open: false, item: null });
    const prevLengthRef = useRef(clients?.length || 0);
    const rootRef = useRef<HTMLDivElement>(null);

    // Watch clients length for scroll-to-last
    useEffect(() => {
        const newLength = clients?.length || 0;
        if (newLength > prevLengthRef.current) {
            setTimeout(() => {
                if (rootRef.current) {
                    scrollToLastMobileCard(rootRef.current);
                }
            }, 0);
        }
        prevLengthRef.current = newLength;
    }, [clients?.length]);

    const getClientSites = useCallback((client: any): number[] => {
        return client.sites || [];
    }, []);

    const getSiteOptions = useCallback((client: any): any[] => {
        if (!client.allSites) return [];
        return client.allSites.map((s: any) => ({ value: s.id, label: s.siteName }));
    }, []);

    const onSitesChange = useCallback((client: any, newSites: number[]) => {
        if (isReadOnly) return;
        onSitesChanged?.(client.id, newSites);
    }, [isReadOnly, onSitesChanged]);

    const confirmDelete = useCallback((client: any) => {
        setDeleteConfirm({ open: true, item: client });
    }, []);

    const doDelete = useCallback(() => {
        const item = deleteConfirm.item;
        if (!item) {
            setDeleteConfirm({ open: false, item: null });
            return;
        }
        onRemoveItem?.('client', item);
        setDeleteConfirm({ open: false, item: null });
    }, [deleteConfirm.item, onRemoveItem]);

    const cancelDelete = useCallback(() => {
        setDeleteConfirm({ open: false, item: null });
    }, []);

    const formatClientName = useCallback((c: any) => {
        return c.name || 'Unknown Client';
    }, []);

    const formatSites = useCallback((c: any) => {
        if (!c.allSites || c.allSites.length === 0) return 'No sites';
        const selectedSites = c.allSites.filter((s: any) => c.sites?.includes(s.id));
        if (selectedSites.length === 0) return 'No sites selected';
        return selectedSites.map((s: any) => s.siteName).join(', ');
    }, []);

    return (
        <div className={'case-report-involved-grid' + (theme ? ' ' + theme : '')} ref={rootRef}>
            <div className="header">
                <h1>Client(s) <span className="hidden-sm hidden-xs">Involved</span></h1>
                <div className="header-buttons">
                    {!isReadOnly && (
                        <button
                            type="button"
                            className="btn-add"
                            onClick={() => onAddExisting?.('client')}
                        >
                            <i className="fas fa-plus"></i> EXISTING
                        </button>
                    )}
                </div>
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: '40%' }}>Name</th>
                            <th style={{ width: '50%' }}>Sites</th>
                            {!isReadOnly && <th style={{ width: '10%' }}>Actions</th>}
                        </tr>
                    </thead>
                    {clients.length === 0 ? (
                        <tbody>
                            <tr>
                                <td colSpan={isReadOnly ? 2 : 3} className="empty-cell">
                                    No clients involved
                                </td>
                            </tr>
                        </tbody>
                    ) : (
                        <tbody>
                            {clients.map((client: any) => (
                                <tr key={client.id}>
                                    <td>
                                        <a href="#" className="item-link" onClick={(e) => { e.preventDefault(); onViewItem?.('client', client.id); }}>
                                            {formatClientName(client)}
                                        </a>
                                    </td>
                                    <td>
                                        {!isReadOnly && client.allSites && client.allSites.length > 0 ? (
                                            <MultiSelect
                                                value={getClientSites(client)}
                                                items={getSiteOptions(client)}
                                                labelKey="label"
                                                valueKey="value"
                                                placeholder="Select Sites"
                                                maxDisplay={2}
                                                disabled={isReadOnly}
                                                onChange={(newSites) => onSitesChange(client, newSites)}
                                            />
                                        ) : (
                                            <span>{formatSites(client)}</span>
                                        )}
                                    </td>
                                    {!isReadOnly && (
                                        <td>
                                            <div className="actions">
                                                <button className="btn-delete" onClick={() => confirmDelete(client)} title="Remove client">
                                                    Remove
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    )}
                </table>
            </div>

            <div className="mobile-cards">
                {clients.length === 0 ? (
                    <div className="empty-state">
                        <p>No clients involved</p>
                    </div>
                ) : (
                    clients.map((client: any) => (
                        <div key={'mobile-' + client.id} className="card">
                            <div className="card-header">
                                <div className="card-header-content">
                                    <a href="#" className="card-item-name" onClick={(e) => { e.preventDefault(); onViewItem?.('client', client.id); }}>
                                        {formatClientName(client)}
                                    </a>
                                </div>
                                {!isReadOnly && <button className="btn-delete" onClick={() => confirmDelete(client)}>Remove</button>}
                            </div>

                            <div className="card-grid">
                                <div className="card-field card-field-full">
                                    <div className="card-field-label">Sites</div>
                                    <div className="card-field-value">
                                        {!isReadOnly && client.allSites && client.allSites.length > 0 ? (
                                            <MultiSelect
                                                value={getClientSites(client)}
                                                items={getSiteOptions(client)}
                                                labelKey="label"
                                                valueKey="value"
                                                placeholder="Select Sites"
                                                maxDisplay={2}
                                                disabled={isReadOnly}
                                                onChange={(newSites) => onSitesChange(client, newSites)}
                                            />
                                        ) : (
                                            <span>{formatSites(client)}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <ConfirmModal
                open={deleteConfirm.open}
                title="Remove Client?"
                messageHtml="Are you sure you want to remove this client from the report?"
                confirmText="Remove"
                cancelText="Cancel"
                confirmClass="danger"
                onConfirm={doDelete}
                onCancel={cancelDelete}
            />
        </div>
    );
}

(window as any).CaseReportClientsInvolvedReact = CaseReportClientsInvolved;
